import React, { useEffect, useRef, useState } from 'react';
import { describeError } from '../../../services/errorMessage';
import { einschaetzeBaubeschreibung, KI_ABGEBROCHEN } from '../../../services/planungKi/planungKiService';
import { ADRESSAT_LABEL } from '../fragenkatalog';
import type { LeitfadenNavigation } from '../leitfadenNavigation';
import type { PlanungAnsicht, PlanungReiterSchluessel } from '../planungAnsicht';
import { kiBeschreibungErgebnis, kiMerkmalAnfragen } from '../planungKiAnzeige';
import type { ProjektMerkmale } from '../types';
import { UnterlagenCheckliste } from '../UnterlagenCheckliste';
import { useEmpfaengerZuordnung } from '../useEmpfaenger';
import {
    AbgeschlossenKarte,
    AbschliessenKarte,
    BedenkenKarte,
    BeschreibungKarte,
    BestaetigenKarte,
    DialogRahmen,
    EmpfaengerKarte,
    FrageKarte,
    FristenKarte,
    GegenstandslosKarte,
    HinweisKarte,
    KatalogKarte,
    KiLiestKarte,
    UnterlagenKarte,
    VersendenKarte,
    WartenKarte,
} from './DialogKarten';
import type { FrageAntwort } from './dialogKartenTypen';
import { abschnittVon, fortschritt, LEERE_SITZUNG, naechsterSchritt, type DialogSchritt, type DialogSitzung } from './dialogLogik';

/** Eine Antwort dieser Sitzung — fuer „Zurück". */
type VerlaufEintrag =
    | { id: string; art: 'uebersprungen' }
    | { id: string; art: 'merkmale'; felder: (keyof ProjektMerkmale)[] }
    | { id: string; art: 'zahl'; feld: keyof ProjektMerkmale }
    | { id: string; art: 'lph' };

/**
 * Der gefuehrte Dialog der Planungsphase (Betreiber 01.11.2026): beim
 * Oeffnen genau eine Karte — die naechste Frage oder die naechste Aufgabe.
 *
 * Welche Karte, rechnet `naechsterSchritt` (rein, getestet). Hier liegt nur,
 * was die App von selbst tut, und was ein Klick ausloest:
 *  - die KI liest die Beschreibung, sobald es eine gibt (kein Knopf mehr);
 *  - ist die letzte Frage beantwortet, entsteht der Fragenkatalog;
 *  - passen nach geaenderten Angaben neue Fragen, werden sie ergaenzt.
 * Alles andere — Versand, Feststellen, Bedenkenanzeige, Abschluss — bleibt
 * ein Klick des Menschen, und jede Karte sagt, welcher.
 *
 * Schreibwege sind dieselben wie in „Alle Bereiche" (`a`): Antworten gehen
 * in den Entwurf der Grundlagen und speichern sich selbst
 * (`autoSpeichern.ts`). Die Sitzung (Weiß nicht, Später, Stimmt nicht) liegt
 * nur im Speicher; der Container haengt den Dialog je Projekt neu ein.
 */
export const PlanungDialog: React.FC<{
    a: PlanungAnsicht;
    nav: LeitfadenNavigation;
    meldungen: React.ReactNode;
    /** Nach „Alle Bereiche" wechseln, auf den genannten Reiter. */
    zuBereich: (r: PlanungReiterSchluessel) => void;
}> = ({ a, nav, meldungen, zuBereich }) => {
    const [sitzung, setSitzung] = useState<DialogSitzung>(LEERE_SITZUNG);
    const [verlauf, setVerlauf] = useState<VerlaufEintrag[]>([]);
    const g = a.grundlagen;

    // Wie Station 5: dieselbe Sperre und dieselbe Phase wie die Fragenliste.
    const planungId = a.phase ? a.phase.id : (a.fragen.length ? a.fragen[0].planung_id : null);
    const empfaenger = useEmpfaengerZuordnung(planungId, a.fragenListe.isReadOnly);

    const schritt = naechsterSchritt({
        projectId: a.projectId,
        laden: a.laden,
        fehler: a.fehler,
        isReadOnly: a.isReadOnly,
        abgeschlossen: a.abgeschlossen,
        phase: a.phase,
        fragen: a.fragen,
        hinweise: a.hinweise,
        heuteIso: a.heuteIso,
        entwurf: { baubeschreibung: g.baubeschreibung, bauaufgabe: g.bauaufgabe, merkmale: g.merkmale, lph: g.lph },
        vorschlaege: a.vorschlagsLeiste.vorschlaege,
        vorschlaegeAusgeblendet: a.vorschlagsLeiste.ausgeblendet,
        ki: { ergebnis: a.kiLeiste.ergebnis, laeuft: a.kiLaeuft, ausgeblendet: a.kiLeiste.ausgeblendet },
        staende: nav.staende,
        nachziehbar: a.nachziehbar.length,
        nichtMehrZutreffend: a.gegenstandslosBanner.fragen.length,
        fristenAnzahl: a.fristenBanner.anzahl,
        empfaenger: {
            zuordnung: empfaenger.zuordnung,
            laden: empfaenger.laden,
            speichert: empfaenger.stand === 'speichert',
            fehler: empfaenger.stand === 'fehler' ? (empfaenger.fehler || 'unbekannter Fehler') : null,
            hinweis: empfaenger.hinweis,
        },
        empfaengerQuellen: a.fragenListe.empfaengerQuellen,
        versandSperrgrund: a.versandSperrgrund,
        sitzung,
    });

    // ─── Die KI liest von selbst ─────────────────────────────────────────
    // Ein Lauf je Sitzung und Projekt (`kiSollLesen`: ein Ergebnis fuer
    // dieses Projekt beendet ihn, auch ein Fehler). Der Ref haelt den
    // laufenden Aufruf: solange er steht, startet kein zweiter. Beim
    // Aushaengen bricht der Lauf ab, der Ref wird frei und die Sperre des
    // Containers faellt sofort — so startet ein erneutes Einhaengen (auch das
    // doppelte der Entwicklung) wieder, und nur der juengste Lauf meldet sein
    // Ende (Opus-Review 01.11.: sonst blieb die Lesekarte stehen).
    const kiLauf = useRef<AbortController | null>(null);
    const meldeKiLauf = a.kiLeiste.onLaeuft;
    useEffect(() => () => {
        const lauf = kiLauf.current;
        if (!lauf) return;
        kiLauf.current = null;
        lauf.abort();
        meldeKiLauf?.(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const liesBeschreibung = async () => {
        const k = a.kiLeiste;
        const anfragen = kiMerkmalAnfragen(k.merkmale);
        if (!anfragen.length || !String(k.beschreibung || '').trim()) return;
        const ctrl = new AbortController();
        kiLauf.current = ctrl;
        k.onLaeuft?.(true);
        try {
            const lauf = await einschaetzeBaubeschreibung(k.beschreibung, anfragen, { signal: ctrl.signal });
            if (ctrl.signal.aborted || lauf.fehler === KI_ABGEBROCHEN) return;
            k.onErgebnis(kiBeschreibungErgebnis(k.projectId, anfragen, lauf, new Date().toISOString()));
        } catch (e) {
            if (!ctrl.signal.aborted) {
                k.onErgebnis(kiBeschreibungErgebnis(k.projectId, anfragen, null, new Date().toISOString(), describeError(e)));
            }
        } finally {
            // Nur der juengste Lauf gibt die Sperre frei — ein beim
            // Aushaengen abgebrochener hat das schon getan.
            if (kiLauf.current === ctrl) {
                kiLauf.current = null;
                k.onLaeuft?.(false);
            }
        }
    };

    useEffect(() => {
        if (schritt.art !== 'ki_liest' || a.kiLaeuft || kiLauf.current) return;
        void liesBeschreibung();
        // liesBeschreibung liest `a` zum Zeitpunkt des Starts — mehr braucht es nicht.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schritt.art, a.kiLaeuft]);

    // ─── Der Katalog entsteht von selbst ─────────────────────────────────
    // Von selbst genau einmal je Sitzung; danach nur noch „Erneut versuchen".
    // Der Ref verhindert einen zweiten Aufruf, solange der erste laeuft
    // (doppelter Effekt in der Entwicklung, schnelles Neuzeichnen). Scheitert
    // er — oder meldet Erfolg, ohne dass ein Katalog zu sehen ist —, steht
    // die Karte mit „Erneut versuchen" da, statt endlos neu anzulegen.
    const [katalogStand, setKatalogStand] = useState<'bereit' | 'laeuft' | 'fertig' | 'fehler'>('bereit');
    const katalogLaeuft = useRef(false);
    const erzeugeKatalog = () => {
        if (katalogLaeuft.current) return;
        katalogLaeuft.current = true;
        setKatalogStand('laeuft');
        void a.erzeugeKatalog().then(ok => {
            katalogLaeuft.current = false;
            setKatalogStand(ok ? 'fertig' : 'fehler');
        });
    };
    useEffect(() => {
        if (schritt.art !== 'katalog' || katalogStand !== 'bereit' || a.laeuft || a.isReadOnly) return;
        erzeugeKatalog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schritt.art, katalogStand, a.laeuft, a.isReadOnly]);

    // ─── Neue Fragen ergaenzen sich von selbst ───────────────────────────
    // Nachziehen fuegt nur hinzu (`neueFragenNachziehen`), entfernt nie. Von
    // selbst genau einmal je Sitzung: scheitert es (oder bleibt danach etwas
    // nachziehbar), bietet die Karte „Erneut versuchen" — keine Schleife,
    // und verschickt wird erst, wenn der Katalog vollstaendig ist.
    const [nachziehStand, setNachziehStand] = useState<'bereit' | 'laeuft' | 'fertig' | 'fehler'>('bereit');
    const nachziehLaeuft = useRef(false);
    const zieheNach = () => {
        if (nachziehLaeuft.current) return;
        nachziehLaeuft.current = true;
        setNachziehStand('laeuft');
        void a.zieheNach().then(ok => {
            nachziehLaeuft.current = false;
            setNachziehStand(ok ? 'fertig' : 'fehler');
        });
    };
    useEffect(() => {
        if (schritt.art !== 'nachziehen' || nachziehStand !== 'bereit' || a.laeuft) return;
        zieheNach();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schritt.art, nachziehStand, a.laeuft]);
    // Ist ergaenzt und der Schritt vorbei, darf ein spaeteres Nachziehen in
    // derselben Sitzung (andere Merkmale) wieder von selbst laufen. Zustand
    // beim Rendern anpassen statt in einem Effekt (React-Muster).
    if (schritt.art !== 'nachziehen' && nachziehStand === 'fertig') setNachziehStand('bereit');

    // ─── Antworten ───────────────────────────────────────────────────────

    const mitEintrag = <T,>(menge: ReadonlySet<T>, wert: T): Set<T> => new Set(menge).add(wert);
    const ohneEintrag = <T,>(menge: ReadonlySet<T>, wert: T): Set<T> => {
        const rest = new Set(menge);
        rest.delete(wert);
        return rest;
    };

    const beantworte = (s: Extract<DialogSchritt, { art: 'frage' }>, antwort: FrageAntwort) => {
        const f = s.frage;
        if (antwort.art === 'weiss_nicht') {
            setSitzung(x => ({ ...x, uebersprungen: mitEintrag(x.uebersprungen, f.id) }));
            setVerlauf(v => [...v, { id: f.id, art: 'uebersprungen' }]);
            return;
        }
        const feld = f.feld;
        if (feld.art === 'ja_nein' && antwort.art === 'ja_nein') {
            g.onMerkmal(feld.feld, antwort.wert);
            setVerlauf(v => [...v, { id: f.id, art: 'merkmale', felder: [feld.feld] }]);
        } else if (feld.art === 'zahl' && antwort.art === 'zahl') {
            g.onZahl(feld.feld, antwort.wert);
            setVerlauf(v => [...v, { id: f.id, art: 'zahl', feld: feld.feld }]);
        } else if (feld.art === 'lph' && antwort.art === 'lph') {
            g.onLph(antwort.wert);
            setVerlauf(v => [...v, { id: f.id, art: 'lph' }]);
        } else if (feld.art === 'gruppe' && antwort.art === 'gruppe') {
            // Jede Merkmalsaenderung baut im Container auf der vorigen auf
            // (`aendereEntwurf` liest den Ref) — mehrere Aufrufe hier sind sicher.
            feld.felder.forEach(k => g.onMerkmal(k, antwort.ja.includes(k)));
            setVerlauf(v => [...v, { id: f.id, art: 'merkmale', felder: [...feld.felder] }]);
        }
    };

    /** Die letzte Antwort dieser Sitzung zuruecknehmen — die Frage kommt wieder. */
    const zurueck = () => {
        const letzter = verlauf[verlauf.length - 1];
        if (!letzter) return;
        setVerlauf(v => v.slice(0, -1));
        if (letzter.art === 'uebersprungen') {
            setSitzung(x => {
                const rest = new Set(x.uebersprungen);
                rest.delete(letzter.id);
                return { ...x, uebersprungen: rest };
            });
        } else if (letzter.art === 'merkmale') {
            letzter.felder.forEach(k => g.onMerkmal(k, undefined));
        } else if (letzter.art === 'zahl') {
            g.onZahl(letzter.feld, undefined);
        } else {
            g.onLph(null);
        }
    };

    const stimmtSo = (s: Extract<DialogSchritt, { art: 'bestaetigen' }>) => {
        const ausProjekt = s.zeilen.flatMap(z => (z.vorschlag ? [z.vorschlag] : []));
        if (ausProjekt.length) a.vorschlagsLeiste.onAlleUebernehmen(ausProjekt);
        s.zeilen.forEach(z => { if (z.ki) a.kiLeiste.onUebernehmen(z.ki); });
    };

    const ersterEntwurf = () => a.hinweise.find(h => !h.versendet_am && (h.art === 'bedenken' || h.art === 'gefahr'));

    // ─── Karte ───────────────────────────────────────────────────────────

    const antwortFuss = a.antwortMailMoeglich ? (
        <span>
            Antwort eingegangen?{' '}
            <button type="button" onClick={() => a.oeffneAntwortMail()} className="font-bold text-blue-700 hover:underline">
                Mail einfügen
            </button>
        </span>
    ) : undefined;

    const karte = (): React.ReactNode => {
        switch (schritt.art) {
            case 'laden':
                return <HinweisKarte art="laden" />;
            case 'fehler':
                return <HinweisKarte art="fehler" text={schritt.text} onAlleBereiche={() => zuBereich('grundlagen')} />;
            case 'nur_ansicht':
                return <HinweisKarte art="nur_ansicht" onAlleBereiche={() => zuBereich('grundlagen')} />;
            case 'beschreibung':
                return (
                    <BeschreibungKarte
                        key="beschreibung"
                        vorlage={schritt.vorlage}
                        ausProjekt={schritt.ausProjekt}
                        onWeiter={text => {
                            const t = text.trim();
                            if (t && t !== g.baubeschreibung.trim()) g.onChange('baubeschreibung', t);
                            setSitzung(x => ({ ...x, beschreibungFertig: true, kiAus: x.kiAus || !t }));
                        }}
                    />
                );
            case 'ki_liest':
                return (
                    <KiLiestKarte
                        onAbbrechen={() => {
                            kiLauf.current?.abort();
                            setSitzung(x => ({ ...x, kiAus: true }));
                        }}
                    />
                );
            case 'bestaetigen':
                return (
                    <BestaetigenKarte
                        zeilen={schritt.zeilen}
                        kiFehler={schritt.kiFehler}
                        onStimmtSo={() => stimmtSo(schritt)}
                        onVerwerfen={id => setSitzung(x => ({ ...x, verworfen: mitEintrag(x.verworfen, id) }))}
                    />
                );
            case 'frage':
                return (
                    <FrageKarte
                        key={schritt.frage.id}
                        frage={schritt.frage}
                        hinweise={schritt.hinweise}
                        nummer={schritt.nummer}
                        gesamt={schritt.gesamt}
                        kiFehler={schritt.kiFehler}
                        onAntwort={antwort => beantworte(schritt, antwort)}
                        onZurueck={verlauf.length ? zurueck : undefined}
                    />
                );
            case 'katalog':
                return (
                    <KatalogKarte
                        laeuft={a.laeuft || katalogStand === 'bereit' || katalogStand === 'laeuft'}
                        fehlgeschlagen={katalogStand === 'fehler' || katalogStand === 'fertig'}
                        onErneut={erzeugeKatalog}
                    />
                );
            case 'nachziehen':
                // Dieselbe Karte wie beim Anlegen: erst „Ich stelle …", nach
                // einem Fehlschlag „Erneut versuchen".
                return (
                    <KatalogKarte
                        laeuft={a.laeuft || nachziehStand === 'bereit' || nachziehStand === 'laeuft'}
                        fehlgeschlagen={nachziehStand === 'fehler' || nachziehStand === 'fertig'}
                        onErneut={zieheNach}
                    />
                );
            case 'gegenstandslos':
                return (
                    <GegenstandslosKarte
                        anzahl={schritt.anzahl}
                        laeuft={a.laeuft}
                        onSetzen={() => a.gegenstandslosBanner.onSetzen()}
                        onSpaeter={() => setSitzung(x => ({ ...x, gegenstandslosSpaeter: true }))}
                    />
                );
            case 'empfaenger': {
                const adressat = schritt.adressat;
                return (
                    <EmpfaengerKarte
                        key={`empfaenger:${adressat}`}
                        adressatLabel={schritt.adressatLabel}
                        anzahl={schritt.anzahl}
                        vorschlaege={schritt.vorschlaege}
                        bisher={schritt.bisher}
                        hinweis={schritt.hinweis}
                        schreibbar={empfaenger.schreibbar}
                        onWaehle={e => {
                            if (!empfaenger.setze(adressat, e)) return;
                            setSitzung(x => ({ ...x, empfaengerWechsel: ohneEintrag(x.empfaengerWechsel, adressat) }));
                        }}
                        onSpaeter={() => setSitzung(x => ({
                            ...x,
                            versandSpaeter: mitEintrag(x.versandSpaeter, adressat),
                            empfaengerWechsel: ohneEintrag(x.empfaengerWechsel, adressat),
                        }))}
                    />
                );
            }
            case 'versenden': {
                const adressat = schritt.adressat;
                return (
                    <VersendenKarte
                        adressatLabel={schritt.adressatLabel}
                        anzahl={schritt.anzahl}
                        empfaenger={schritt.empfaenger}
                        sperrgrund={schritt.sperrgrund}
                        danach={schritt.danach}
                        laeuft={a.laeuft}
                        onSenden={() => a.oeffneVersand(adressat)}
                        // Nur die Auswahl zeigen — der bisherige Eintrag bleibt,
                        // bis ein neuer gewaehlt ist (Opus-Review 01.11.).
                        onAndererEmpfaenger={() => setSitzung(x => ({ ...x, empfaengerWechsel: mitEintrag(x.empfaengerWechsel, adressat) }))}
                        onSpaeter={() => setSitzung(x => ({ ...x, versandSpaeter: mitEintrag(x.versandSpaeter, adressat) }))}
                    />
                );
            }
            case 'fristen':
                return (
                    <FristenKarte
                        anzahl={schritt.anzahl}
                        laeuft={a.laeuft}
                        onFeststellen={() => a.fristenBanner.onFeststellen()}
                        onAnsehen={() => zuBereich('fragen')}
                    />
                );
            case 'bedenken': {
                const ids = schritt.ids;
                return (
                    <BedenkenKarte
                        anzahl={ids.length}
                        entwuerfe={schritt.entwuerfe}
                        laeuft={a.laeuft}
                        onVorbereiten={() => a.oeffneBedenken(ids)}
                        onEntwurfOeffnen={() => {
                            const h = ersterEntwurf();
                            if (h) a.hinweisListe.onOeffnen(h);
                        }}
                    />
                );
            }
            case 'unterlagen':
                return (
                    <UnterlagenKarte
                        kurz={schritt.kurz}
                        onSpaeter={() => setSitzung(x => ({ ...x, unterlagenSpaeter: true }))}
                    >
                        <UnterlagenCheckliste {...a.unterlagenCheckliste} />
                    </UnterlagenKarte>
                );
            case 'abschliessen':
                return (
                    <AbschliessenKarte
                        folgeunterlagen={a.folgeunterlagen}
                        restrisiken={a.freigabeKasten.restrisiken}
                        laeuft={a.laeuft}
                        onAbschliessen={arten => a.freigeben(arten)}
                    />
                );
            case 'warten':
                return (
                    <WartenKarte
                        wartend={schritt.wartend}
                        naechsteFrist={schritt.naechsteFrist}
                        nebenbei={schritt.nebenbei}
                        spaeter={schritt.spaeter.map(x => ADRESSAT_LABEL[x])}
                        nurAnsicht={schritt.nurAnsicht}
                        antwortMailMoeglich={a.antwortMailMoeglich}
                        onAntwortMail={() => a.oeffneAntwortMail()}
                        onVonHand={() => zuBereich('fragen')}
                        onSpaeterJetzt={() => setSitzung(x => ({ ...x, versandSpaeter: new Set() }))}
                    />
                );
            case 'abgeschlossen':
                return (
                    <AbgeschlossenKarte
                        am={schritt.am}
                        folgeunterlagen={a.folgeunterlagen}
                        isReadOnly={a.isReadOnly}
                        laeuft={a.laeuft}
                        onWiederOeffnen={() => a.wiedereroeffnen()}
                    />
                );
        }
    };

    const abschnitt = abschnittVon(schritt);
    const fuss = abschnitt === 'antworten' && schritt.art !== 'warten' ? antwortFuss : undefined;

    return (
        <div>
            {meldungen}
            <DialogRahmen fortschritt={fortschritt(schritt)} fuss={fuss}>
                {karte()}
            </DialogRahmen>
        </div>
    );
};
